import { useState, useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, CardContent, CardHeader } from "@learn-me/ui";
import {
  HelpCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  Sparkles,
  CheckCircle,
  AlertCircle,
  Trash2,
  Eye,
} from "lucide-react";
import { adminApi, type GeneratedQuiz } from "../../api/adminApi";
import { api } from "@/lib/api";
import { QuizPreviewModal } from "../QuizPreviewModal";
import toast from "react-hot-toast";
import type { ApiResponse } from "@learn-me/types";

interface Quiz {
  id: string;
  title: string;
  passingScore: number;
  questions: Array<{
    id: string;
    text: string;
    explanation?: string;
    options: Array<{
      id: string;
      text: string;
      isCorrect: boolean;
    }>;
  }>;
}

interface ChapterQuizPanelProps {
  chapterId: string;
  courseId: string;
  hasSections: boolean;
  minQuizScore?: number;
}

export function ChapterQuizPanel({
  chapterId,
  courseId,
  hasSections,
  minQuizScore = 70,
}: ChapterQuizPanelProps) {
  const queryClient = useQueryClient();
  const [isExpanded, setIsExpanded] = useState(true);
  const [generatedQuiz, setGeneratedQuiz] = useState<GeneratedQuiz | null>(
    null,
  );
  const [useReferenceDocs, setUseReferenceDocs] = useState(true);
  const [showExistingQuiz, setShowExistingQuiz] = useState(false);
  const [showQuizModal, setShowQuizModal] = useState(false);

  // Fetch existing quiz - only if chapterId is valid
  const { data: existingQuiz, isLoading: loadingQuiz } = useQuery({
    queryKey: ["chapter-quiz", chapterId],
    queryFn: async () => {
      // Validate chapterId is a valid ID (UUID or CUID) before making the request
      const isValidId =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          chapterId,
        ) || /^c[a-z0-9]{20,}$/i.test(chapterId);
      if (!chapterId || !isValidId) {
        console.warn("[ChapterQuizPanel] Invalid chapterId:", chapterId);
        return null;
      }
      try {
        const response = await api.get<ApiResponse<Quiz>>(
          `/quizzes/chapter/${chapterId}`,
        );
        return response.data.data;
      } catch {
        return null;
      }
    },
    enabled: !!chapterId,
  });

  // Check if course has reference docs
  const { data: referenceDocs = [] } = useQuery({
    queryKey: ["course-reference-docs", courseId],
    queryFn: () => adminApi.getCourseReferenceDocs(courseId),
    staleTime: 30000,
  });

  const hasReferenceDocs = referenceDocs.length > 0;
  const processedDocsCount = referenceDocs.filter((d) => d.isProcessed).length;

  // Generate quiz mutation
  const generateQuizMutation = useMutation({
    mutationFn: () => adminApi.generateChapterQuiz(chapterId, useReferenceDocs),
    onSuccess: (data) => {
      setGeneratedQuiz(data);
      toast.success("Quiz generado exitosamente");
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Error al generar quiz",
      );
    },
  });

  // Save quiz mutation
  const saveQuizMutation = useMutation({
    mutationFn: async () => {
      if (!generatedQuiz) throw new Error("No hay quiz para guardar");

      const payload = {
        chapterId,
        title: generatedQuiz.title,
        passingScore: minQuizScore,
        questions: generatedQuiz.questions.map((q, idx) => ({
          text: q.text,
          type: "SINGLE_CHOICE",
          explanation: q.explanation,
          points: 1,
          order: idx + 1,
          options: q.options,
        })),
      };

      if (existingQuiz) {
        await api.put(`/quizzes/${existingQuiz.id}`, payload);
        return;
      }

      await api.post("/quizzes", payload);
    },
    onSuccess: () => {
      toast.success("Quiz guardado exitosamente");
      setGeneratedQuiz(null);
      queryClient.invalidateQueries({ queryKey: ["chapter-quiz", chapterId] });
    },
    onError: () => {
      toast.error("Error al guardar el quiz");
    },
  });

  // Delete quiz mutation
  const deleteQuizMutation = useMutation({
    mutationFn: async () => {
      if (!existingQuiz) throw new Error("No hay quiz para eliminar");
      await api.delete(`/quizzes/${existingQuiz.id}`);
    },
    onSuccess: () => {
      toast.success("Quiz eliminado");
      queryClient.invalidateQueries({ queryKey: ["chapter-quiz", chapterId] });
    },
    onError: () => {
      toast.error("Error al eliminar el quiz");
    },
  });

  const handleSaveQuiz = useCallback(async () => {
    saveQuizMutation.mutate();
  }, [saveQuizMutation]);

  const handleDiscardQuiz = useCallback(() => {
    setGeneratedQuiz(null);
  }, []);

  const handleDeleteQuiz = useCallback(() => {
    if (
      window.confirm("¿Eliminar este quiz? Esta acción no se puede deshacer.")
    ) {
      deleteQuizMutation.mutate();
    }
  }, [deleteQuizMutation]);

  const hasQuiz = !!existingQuiz;
  const isGenerating = generateQuizMutation.isPending;

  return (
    <Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
      <CardHeader className="py-3 px-4">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center justify-between w-full"
        >
          <div className="flex items-center gap-2">
            <HelpCircle className="h-4 w-4 text-green-600" />
            <span className="font-medium text-sm text-green-800">Quiz</span>
            {hasQuiz && (
              <span className="text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-full">
                {existingQuiz?.questions.length} preguntas
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {hasQuiz && <CheckCircle className="h-4 w-4 text-green-500" />}
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-green-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-green-400" />
            )}
          </div>
        </button>
      </CardHeader>

      {isExpanded && (
        <CardContent className="px-4 pb-4 pt-0 space-y-4">
          {loadingQuiz ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-green-500" />
            </div>
          ) : generatedQuiz ? (
            // Show generated quiz summary + modal
            <>
              <div className="p-3 bg-white/60 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-4 w-4 text-green-600" />
                  <p className="text-sm font-medium text-green-800">
                    Quiz Generado
                  </p>
                </div>
                <p className="text-xs text-green-700 mb-1">
                  {generatedQuiz.title}
                </p>
                <p className="text-xs text-green-600 mb-3">
                  {generatedQuiz.questions.length} preguntas listas para revisar
                </p>
                <Button
                  type="button"
                  size="sm"
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => setShowQuizModal(true)}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Revisar y Guardar
                </Button>
              </div>
              <QuizPreviewModal
                quiz={generatedQuiz}
                open={showQuizModal}
                onOpenChange={setShowQuizModal}
                onSave={handleSaveQuiz}
                onDiscard={handleDiscardQuiz}
                isSaving={saveQuizMutation.isPending}
              />
            </>
          ) : hasQuiz ? (
            // Show existing quiz info
            <div className="space-y-3">
              <div className="p-3 bg-white/60 rounded-lg border border-green-200">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm font-medium text-green-800">
                      {existingQuiz?.title}
                    </p>
                    <p className="text-xs text-green-600">
                      {existingQuiz?.questions.length} preguntas | Puntaje
                      mínimo: {existingQuiz?.passingScore}%
                    </p>
                  </div>
                </div>

                {/* Show/hide questions */}
                <button
                  type="button"
                  onClick={() => setShowExistingQuiz(!showExistingQuiz)}
                  className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700"
                >
                  <Eye className="h-3 w-3" />
                  {showExistingQuiz ? "Ocultar preguntas" : "Ver preguntas"}
                </button>

                {showExistingQuiz && existingQuiz && (
                  <div className="mt-3 space-y-2 max-h-[300px] overflow-y-auto">
                    {existingQuiz.questions.map((q, idx) => (
                      <div
                        key={q.id}
                        className="p-2 bg-white rounded border border-green-100"
                      >
                        <p className="text-sm font-medium text-gray-800">
                          {idx + 1}. {q.text}
                        </p>
                        <div className="mt-1 space-y-0.5">
                          {q.options.map((opt) => (
                            <p
                              key={opt.id}
                              className={`text-xs pl-4 ${
                                opt.isCorrect
                                  ? "text-green-600 font-medium"
                                  : "text-gray-500"
                              }`}
                            >
                              {opt.isCorrect ? "✓" : "○"} {opt.text}
                            </p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions for existing quiz */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-1 border-green-300 text-green-700 hover:bg-green-100"
                  onClick={() => generateQuizMutation.mutate()}
                  disabled={isGenerating || !hasSections}
                >
                  {isGenerating ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-2" />
                  )}
                  Regenerar
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  onClick={handleDeleteQuiz}
                  disabled={deleteQuizMutation.isPending}
                >
                  {deleteQuizMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          ) : (
            // No quiz - show generation UI
            <div className="space-y-3">
              <p className="text-sm text-green-700">
                Genera un quiz automáticamente basado en el contenido del
                capítulo.
              </p>

              {/* Reference docs toggle */}
              {hasReferenceDocs && (
                <div className="p-2 bg-white/50 rounded-lg border border-green-200">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={useReferenceDocs}
                      onChange={(e) => setUseReferenceDocs(e.target.checked)}
                      className="mt-0.5 w-4 h-4 text-green-600 rounded focus:ring-green-500"
                      disabled={isGenerating}
                    />
                    <div>
                      <span className="text-xs font-medium text-green-800">
                        Usar documentos de referencia
                      </span>
                      <p className="text-xs text-green-600">
                        {processedDocsCount} de {referenceDocs.length}{" "}
                        procesados
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {!hasSections && (
                <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  El capítulo necesita secciones para generar el quiz
                </div>
              )}

              <Button
                type="button"
                className="w-full bg-green-600 text-white hover:bg-green-700"
                onClick={() => generateQuizMutation.mutate()}
                disabled={isGenerating || !hasSections}
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generando...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generar Quiz con IA
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Error state */}
          {generateQuizMutation.isError && (
            <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
              <AlertCircle className="h-4 w-4" />
              Error al generar. Intenta de nuevo.
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
